import { useEffect, type ReactElement } from 'react';
import { isRouteErrorResponse, useRouteError, useLocation } from 'react-router';
import { reportBrowserFailure } from '../telemetry';
import { isApiError } from '../api';
import { Button } from '../ui';
import { OutsideCard, OutsidePage } from './OutsidePage';

/**
 * When a page can't load before the shell exists (the server is down, or answered something
 * unexpected). Calm, no codes, and one way forward.
 */
export function AppUnavailable(): ReactElement {
  const error = useRouteError();
  const { pathname } = useLocation();
  useEffect(() => {
    reportBrowserFailure(error, 'route_error', pathname);
  }, [error, pathname]);
  const offline = isApiError(error) && (error.status === 0 || error.status >= 500);
  const forbidden =
    (isApiError(error) && error.status === 403) ||
    (isRouteErrorResponse(error) && error.status === 403);
  return (
    <OutsidePage title="MyRewards isn’t available">
      <OutsideCard
        icon={forbidden ? 'lock' : 'info'}
        tone={forbidden ? 'problem' : 'info'}
        title={
          forbidden
            ? 'You don’t have access to this'
            : offline
              ? 'MyRewards isn’t answering'
              : 'This page didn’t load'
        }
        actions={
          <Button
            variant="navy"
            onPress={() => {
              window.location.reload();
            }}
          >
            Try again
          </Button>
        }
      >
        <p>
          {forbidden
            ? 'Your account can’t open this page. If you think it should, ask the Stock Admin team.'
            : 'Nothing was changed. Wait a moment and try again; if it keeps happening, contact the Stock Admin team.'}
        </p>
      </OutsideCard>
    </OutsidePage>
  );
}
